import type { EnclosureDetailsBaseData, EnclosureDetailsFlangeData } from "@stryker/core/types/mechanicalJobs";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { filterMaterialGroupsByDesignCode } from "@/utils/materialFilters";

import ExpandableSection from "../../../../../components/job/ExpandableSection";
import { FlangeClampForm } from "./FlangeClampForm";
import { useEnclosureForm } from "./hooks/useEnclosureForm";
import { MaterialMatchingQuestion } from "./MaterialMatchingQuestion";
import { StandardEnclosureForm } from "./StandardEnclosureForm";

const cardName = "enclosureDetails";

/**
 * EnclosureDetails Component
 *
 * Main component for managing enclosure material details in mechanical jobs.
 * Handles different repair types:
 * - flange_clamp: Uses FlangeClampForm
 * - tee, straight_line, long_radius: Uses StandardEnclosureForm
 *
 * Features:
 * - Material matching question (match enclosure to component materials)
 * - Dynamic form fields based on repair type
 * - Grouped material options from API
 * - Special handling for tee type (riser shell selection)
 * - Form validation with Zod
 * - State persistence via Zustand store
 *
 * @example
 * ```tsx
 * <EnclosureDetails />
 * ```
 */
export default function EnclosureDetails() {
  const { toggleExpandableSection, sections } = useCUMechanicalJobStore();
  const savedEnclosureDetails = sections.enclosureDetails;
  const repairType = sections.repairType.data.repairType;
  const designCode = sections.jobDetails.data.design_code;

  /**
   * Fetch and transform enclosure material options from API
   */
  const createOptions = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      const shells = response.shells ?? response.enclosure_shells ?? [];
      return {
        enclosureShellOptions: shells,
        endplateOptions: response.endplates,
        earOptions: response.ears,
        studBoltOptions: response.stud_bolts,
        flangeClampOptions: response.flange_clamps,
      };
    },
  });
  const filteredOptions = useMemo(() => {
    return {
      enclosureShellOptions: filterMaterialGroupsByDesignCode(createOptions.data?.enclosureShellOptions, designCode),
      endplateOptions: filterMaterialGroupsByDesignCode(createOptions.data?.endplateOptions, designCode),
      earOptions: filterMaterialGroupsByDesignCode(createOptions.data?.earOptions, designCode),
      studBoltOptions: filterMaterialGroupsByDesignCode(createOptions.data?.studBoltOptions, designCode),
      flangeClampOptions: filterMaterialGroupsByDesignCode(createOptions.data?.flangeClampOptions, designCode),
    };
  }, [createOptions.data, designCode]);

  const {
    form,
    matchMaterials,
    showRiserMaterial,
    handleSubmit,
    handleMaterialMatchingAnswer,
    handleRiserMaterialChange,
    handleResetEnclosure,
  } = useEnclosureForm();
  const showReset = savedEnclosureDetails.isDataFilled;
  const savedData = savedEnclosureDetails.data as EnclosureDetailsBaseData | EnclosureDetailsFlangeData;
  const matchFlag = savedData.match_enclosure_to_component_material;
  const materialsComplete = savedEnclosureDetails.isDataFilled;

  const findMaterialLabel = (
    value: string | undefined,
    groups?: Array<{
      group: string;
      options: Array<{
        id: number;
        material: string;
        type: string;
      }>;
    }>
  ) => {
    if (!value || !groups) {
      return value;
    }
    const numericValue = Number(value);
    for (const group of groups) {
      const match = group.options.find((option) => option.id === numericValue);
      if (match) {
        return `${match.material} - ${match.type}`;
      }
    }
    return value;
  };

  const summaryItems = useMemo(() => {
    if (!materialsComplete) {
      return [];
    }
    if (matchFlag) {
      return [{ label: "Materials", value: "Matched to component materials" }];
    }
    if (repairType === "flange_clamp") {
      const data = savedData as EnclosureDetailsFlangeData;
      return [
        { label: "Flange Clamp", value: findMaterialLabel(data.flange_clamp, filteredOptions.flangeClampOptions) },
        { label: "Stud Bolts", value: findMaterialLabel(data.stud_bolts, filteredOptions.studBoltOptions) },
      ].filter((item) => item.value);
    }
    const data = savedData as EnclosureDetailsBaseData;
    const riserValue =
      repairType === "tee" && !data.riser_shells ? "Same as header shell" : data.riser_shells || undefined;
    return [
      {
        label: repairType === "tee" ? "Header Shell" : "Enclosure Shell(s)",
        value: findMaterialLabel(data.shells, filteredOptions.enclosureShellOptions),
      },
      { label: "Endplates", value: findMaterialLabel(data.endplates, filteredOptions.endplateOptions) },
      { label: "Ears", value: findMaterialLabel(data.ears, filteredOptions.earOptions) },
      { label: "Stud Bolts", value: findMaterialLabel(data.stud_bolts, filteredOptions.studBoltOptions) },
      repairType === "tee"
        ? { label: "Riser Shell", value: findMaterialLabel(riserValue, filteredOptions.enclosureShellOptions) }
        : null,
    ]
      .filter((item) => item && item.value)
      .map((item) => item as { label: string; value: string });
  }, [
    filteredOptions.earOptions,
    filteredOptions.enclosureShellOptions,
    filteredOptions.endplateOptions,
    filteredOptions.flangeClampOptions,
    filteredOptions.studBoltOptions,
    matchFlag,
    materialsComplete,
    repairType,
    savedData,
  ]);

  return (
    <ExpandableSection
      status={savedEnclosureDetails.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Enclosure Details"
    >
      {showReset ? (
        <div className="mb-4 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-gray-600 hover:text-gray-900"
            onClick={handleResetEnclosure}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      ) : null}
      {!materialsComplete && matchMaterials === undefined ? (
        <MaterialMatchingQuestion onAnswer={handleMaterialMatchingAnswer} />
      ) : null}
      {!materialsComplete && matchMaterials !== undefined ? (
        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            {matchMaterials === false && (
              <div className="flex flex-wrap gap-2">
                {repairType === "flange_clamp" ? (
                  <FlangeClampForm
                    form={form}
                    flangeClampOptions={filteredOptions.flangeClampOptions}
                    studBoltOptions={filteredOptions.studBoltOptions}
                  />
                ) : (
                  <StandardEnclosureForm
                    form={form}
                    repairType={repairType}
                    enclosureShellOptions={filteredOptions.enclosureShellOptions}
                    endplateOptions={filteredOptions.endplateOptions}
                    earOptions={filteredOptions.earOptions}
                    studBoltOptions={filteredOptions.studBoltOptions}
                    showRiserMaterial={showRiserMaterial}
                    onRiserMaterialChange={handleRiserMaterialChange}
                  />
                )}
              </div>
            )}
            <Button type="submit" className="self-end">
              Save
            </Button>
          </form>
        </Form>
      ) : null}
      {materialsComplete ? (
        <div className="flex flex-col gap-2 text-sm text-gray-600">
          <p>Material selection saved.</p>
          {summaryItems.length > 0 ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
              {summaryItems.map((item) => (
                <div key={item.label} className="flex flex-wrap gap-1">
                  <span className="font-medium">{item.label}:</span>
                  <span>{item.value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </ExpandableSection>
  );
}
